import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lightbulb, Send, Sparkles, CheckCircle2, Clock, Rocket, XCircle, RefreshCw, MessageSquareText } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";

const CATEGORIES = [
  { k: "funzionalita", l: "Nuova funzionalità" },
  { k: "ui",           l: "Interfaccia / UX" },
  { k: "integrazione", l: "Integrazione (es. WhatsApp, EHR…)" },
  { k: "bug",          l: "Segnalazione bug" },
  { k: "processo",     l: "Miglioramento del flusso clinico" },
  { k: "generale",     l: "Altro / generale" },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.k, c.l]));

const STATUS_META = {
  new:       { l: "Nuovo",         cls: "bg-blue-50 text-blue-700 border-blue-200",       Icon: Sparkles },
  reviewing: { l: "In valutazione",cls: "bg-amber-50 text-amber-800 border-amber-200",    Icon: Clock },
  planned:   { l: "In roadmap",    cls: "bg-purple-50 text-purple-800 border-purple-200", Icon: Rocket },
  done:      { l: "Realizzato",    cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  declined:  { l: "Non pianificato",cls: "bg-slate-100 text-slate-700 border-slate-200",  Icon: XCircle },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.new;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider border ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.l}
    </span>
  );
}

function humanDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function SuggestionsPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [tab, setTab] = useState(isAdmin ? "all" : "mine");
  const [mine, setMine] = useState([]);
  const [adminItems, setAdminItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "funzionalita" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editStatus, setEditStatus] = useState("new");
  const [editNote, setEditNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: m } = await api.get("/suggestions/mine");
      setMine(m);
      if (isAdmin) {
        const { data: a } = await api.get("/admin/suggestions");
        setAdminItems(a.items || []);
        setCounts(a.counts || {});
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (form.title.trim().length < 3) return toast.error("Il titolo è troppo corto.");
    if (form.description.trim().length < 10) return toast.error("Descrivi meglio l'idea (almeno 10 caratteri).");
    setSaving(true);
    try {
      await api.post("/suggestions", form);
      toast.success("Grazie! Il tuo suggerimento è stato inviato all'amministratore.");
      setDialogOpen(false);
      setForm({ title: "", description: "", category: "funzionalita" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  };

  const saveAdmin = async (id) => {
    setSaving(true);
    try {
      await api.patch(`/admin/suggestions/${id}`, { status: editStatus, admin_note: editNote });
      toast.success("Suggerimento aggiornato");
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  };

  const list = tab === "mine" ? mine : adminItems;

  return (
    <div className="max-w-5xl mx-auto px-3 md:px-6 py-4 md:py-6" data-testid="suggestions-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-[#1E3A8A]/80 flex items-center gap-1.5">
            <Lightbulb className="h-3.5 w-3.5" /> Idee & suggerimenti
          </p>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
            Aiutaci a migliorare Synappsi
          </h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Hai un'idea, un'integrazione che ti farebbe risparmiare tempo, un bug da segnalare? Scrivicelo qui —
            leggiamo tutto e la piattaforma cresce con voi.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="suggestion-new-btn"
                className="h-11 rounded-xl bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                           text-white font-semibold shadow-md shadow-emerald-500/10">
          <Lightbulb className="h-4 w-4 mr-2" /> Proponi una novità
        </Button>
      </div>

      {isAdmin && (
        <div className="flex gap-1 flex-wrap text-[12px] font-semibold mb-3">
          <button onClick={() => setTab("all")}
                  data-testid="tab-admin-all"
                  className={`px-3 py-1.5 rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                    tab === "all"
                      ? "bg-gradient-to-r from-[#1E3A8A] to-[#10B981] text-white shadow-sm"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}>
            Tutti <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/20">{adminItems.length}</span>
          </button>
          {Object.keys(STATUS_META).map((s) => (
            <button key={s} onClick={() => setTab(s)}
                    data-testid={`tab-admin-${s}`}
                    className={`px-3 py-1.5 rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                      tab === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}>
              {STATUS_META[s].l}
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${tab === s ? "bg-white/20" : "bg-white border border-slate-200"}`}>
                {counts[s] || 0}
              </span>
            </button>
          ))}
          <button onClick={() => setTab("mine")}
                  className={`ml-auto px-3 py-1.5 rounded-md whitespace-nowrap transition-colors ${
                    tab === "mine" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}>
            Le mie proposte
          </button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <SynapseLoader size="md" inline className="mr-2" /> Caricamento…
        </div>
      )}

      {!loading && list.length === 0 && (
        <Card className="p-10 md:p-16 bg-white border-slate-200 text-center rounded-2xl">
          <MessageSquareText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-4">
            {tab === "mine" ? "Non hai ancora inviato suggerimenti."
              : "Nessun suggerimento in questa categoria."}
          </p>
          {tab === "mine" && (
            <Button onClick={() => setDialogOpen(true)}
                    className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
              <Lightbulb className="h-4 w-4 mr-2" /> Proponi la prima novità
            </Button>
          )}
        </Card>
      )}

      {!loading && list.length > 0 && (
        <div className="space-y-3">
          {(tab === "mine" ? mine
             : tab === "all" ? adminItems
             : adminItems.filter((x) => x.status === tab)
          ).map((s) => (
            <Card key={s.id} className="p-4 md:p-5 bg-white border-slate-200 rounded-2xl"
                  data-testid={`suggestion-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <StatusBadge status={s.status} />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
                      {CATEGORY_LABEL[s.category] || s.category}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-slate-900 text-base md:text-lg leading-tight">{s.title}</h3>
                  <p className="text-sm text-slate-700 mt-1.5 leading-relaxed whitespace-pre-wrap">{s.description}</p>
                  {s.admin_note && (
                    <div className="mt-3 p-2 rounded-md border border-emerald-200 bg-emerald-50 text-[13px] text-emerald-900">
                      <span className="font-semibold">Nota amministratore:</span> {s.admin_note}
                    </div>
                  )}
                  <div className="mt-2 text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                    <span>{humanDate(s.created_at)}</span>
                    {(tab !== "mine" || isAdmin) && s.user_name && (
                      <span>· <span className="font-medium text-slate-700">{s.user_name}</span>
                        {s.user_email && <span className="text-slate-400"> ({s.user_email})</span>}
                      </span>
                    )}
                  </div>
                </div>
                {isAdmin && tab !== "mine" && editingId !== s.id && (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingId(s.id); setEditStatus(s.status); setEditNote(s.admin_note || "");
                  }} className="flex-shrink-0"
                          data-testid={`suggestion-edit-${s.id}`}>
                    Aggiorna
                  </Button>
                )}
              </div>

              {isAdmin && editingId === s.id && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-3"
                     data-testid={`suggestion-editor-${s.id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-600">Stato</Label>
                      <Select value={editStatus} onValueChange={setEditStatus}>
                        <SelectTrigger data-testid={`suggestion-status-select-${s.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_META).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Nota per l'utente (facoltativa)</Label>
                    <Textarea value={editNote} onChange={(e) => setEditNote(e.target.value)}
                              maxLength={1000} rows={2}
                              placeholder="Es. Grazie! Lo pianifichiamo per la prossima release."
                              data-testid={`suggestion-note-${s.id}`} />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Annulla</Button>
                    <Button size="sm" onClick={() => saveAdmin(s.id)} disabled={saving}
                            className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                      {saving ? <SynapseLoader size="sm" inline className="mr-2" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                      Salva
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Dialog nuovo suggerimento */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="suggestion-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" /> Proponi una novità
            </DialogTitle>
            <DialogDescription>
              Ogni suggerimento è visto dall'admin. Ti risponderemo con lo stato (in valutazione, in roadmap, realizzato…).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="suggestion-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (<SelectItem key={c.k} value={c.k}>{c.l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Titolo</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     maxLength={140} placeholder="Es. Firma digitale della prescrizione"
                     data-testid="suggestion-title" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Descrizione dettagliata</Label>
              <Textarea rows={5} maxLength={2000}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Spiega il problema che risolve, come lo useresti in giornata, esempi concreti…"
                        data-testid="suggestion-description" />
              <div className="text-[11px] text-slate-400 text-right mt-0.5">{form.description.length}/2000</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={saving}
                    data-testid="suggestion-submit"
                    className="bg-gradient-to-r from-[#1E3A8A] to-[#10B981] text-white">
              {saving ? <SynapseLoader size="sm" inline className="mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Invia suggerimento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
